import clsx from 'clsx'
import renderCheckbox from 'components/FormControls/checkboxField'
import renderSelect from 'components/FormControls/select'
import { Button, Typography } from 'gu-ui-kit'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, getFormValues, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import styles from 'modules/Forms/Finance/PaymentProfileCreate/payment-profile.module.scss'
import JuridicalFormFields from './subcomponents/Juridical'
import PersonalFormFields from './subcomponents/Personal'
import GridSpinner from 'components/UI/GridSpinner'
import { EmailDisapproved } from 'modules/Forms/Billing/subcomponents'
import { PAYMENT_PROFILE_CREATE } from 'modules/Finance/PaymentProfiles/types'

let PaymentProfileCreate = ({
  initialValues = {},
  profileId = undefined,
  handleSubmit,
  submitting,
  onClose = () => {},
  form,
  anyTouched,
  change,
  untouch,
  loadingSettings,
  legalTypes = [],
  legalTypesCountryCodes = {},
}) => {
  const { t } = useTranslation()
  const selector = getFormValues(form)
  const [lockForm, setLockForm] = useState(false)

  const {
    i18n,
    is_email_confirmed,
    formValues: { method, details, apply_agreement },
    registeredFields,
  } = useSelector((state) => ({
    i18n: state['app'].i18n,
    is_email_confirmed: state['app'].user.is_email_confirmed,
    formValues: selector(state),
    registeredFields: state['form'][form].registeredFields,
  }))

  useEffect(() => {
    setLockForm(!is_email_confirmed)
  }, [is_email_confirmed])

  const methodName = useMemo(() => {
    if (!details?.country_code) {
      return ''
    }

    if (method === 'personal') {
      if (details?.country_code == 'ru') {
        return 'qiwi'
      } else {
        return 'international_personal'
      }
    } else {
      return 'juridical'
    }
  }, [method, details?.country_code])

  const submit = useCallback(
    (values, dispatch) => {
      const registerFieldsNames = Object.keys(registeredFields)
      const regValues = omit(pick(values, [...registerFieldsNames]), [
        'apply_agreement',
        details.country_code === 'ru' && method === 'personal' ? 'details.country_code' : '',
      ])

      return new Promise((resolve, reject) => {
        dispatch({
          meta: { resolve, reject },
          type: PAYMENT_PROFILE_CREATE,
          payload: { ...regValues, method: methodName },
          formName: form,
          profileId,
        })
      }).then(() => {
        onClose()
        global.yaCounter32793525 && global.yaCounter32793525.reachGoal('payment-profile-create')

        global.dataLayer && global.dataLayer.push({ event: 'payment-profile-create' })
      })
    },
    [registeredFields, form, methodName, method],
  )

  useEffect(() => {
    if (profileId) {
      return
    }

    change('details.tax_number', '')
    change('details.country_code', null)

    untouch('details.tax_number')
    untouch('details.country_code', '')
  }, [method])

  useEffect(() => {
    change('method', initialValues?.method || legalTypes[0])
  }, [legalTypes])

  useEffect(() => {
    if (profileId) {
      return
    }

    legalTypesCountryCodes?.[method]?.length === 1 &&
      change('details.country_code', legalTypesCountryCodes[method][0].id)
  }, [legalTypesCountryCodes, method])

  return (
    <form onSubmit={handleSubmit(submit)}>
      <EmailDisapproved isEmailConfirmed={is_email_confirmed} />
      <div className={'margin-bottom-16'}>
        <Field
          ControlProps={{
            startAdornment: loadingSettings && <GridSpinner width={16} />,
          }}
          disabled={loadingSettings || !!profileId || lockForm}
          label={t('payment_profiles::new.fields.method.label')}
          name="method"
          component={renderSelect}
          fullWidth
          t={t}
          showEmptyOption={false}
          emptyOptionLabel={t('common::select_empty_option')}
          clearOptionLabel={t('common::select_clear_option')}
          options={legalTypes.map((type) => ({
            value: type,
            label: t(`payment_profiles::new.fields.method.options.${type}`),
          }))}
        />
      </div>

      <div
        className={clsx('margin-bottom-16', {
          [formStyles['formControl_hidden']]: legalTypesCountryCodes?.[method]?.length === 1,
        })}
      >
        <Field
          ControlProps={{
            startAdornment: loadingSettings && <GridSpinner width={16} />,
          }}
          disabled={loadingSettings || lockForm}
          label={t('payment_profiles::new.fields.details.country_code.label')}
          name="details.country_code"
          component={renderSelect}
          fullWidth
          t={t}
          showEmptyOption={false}
          normalize={(value = '') => value}
          emptyOptionLabel={t('common::select_empty_option')}
          clearOptionLabel={t('common::select_clear_option')}
          options={
            legalTypesCountryCodes?.[method]
              ? legalTypesCountryCodes?.[method].map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))
              : []
          }
        />
      </div>

      {method && (
        <>
          {method === 'personal' ? (
            <PersonalFormFields
              form={form}
              details={details}
              disabled={lockForm}
            />
          ) : (
            <JuridicalFormFields
              details={details}
              form={form}
              disabled={lockForm}
            />
          )}
        </>
      )}

      <Typography
        variant={'p5'}
        component={'div'}
        className={'margin-bottom-16'}
      >
        <Field
          disabled={lockForm}
          name="apply_agreement"
          checked={apply_agreement}
          component={renderCheckbox}
          noWrap={false}
          label={
            <div
              dangerouslySetInnerHTML={{
                __html: t('payment_profiles::new.fields.apply_agreement.label', {
                  i18n,
                  buttonLabel: t(`common::form_buttons.${profileId ? 'save' : 'create'}`),
                }),
              }}
            />
          }
          t={t}
        />
      </Typography>

      <div className={clsx(formStyles['formControl__action-buttons'], styles['action-buttons'])}>
        <Button
          disabled={submitting}
          onClick={onClose}
          variant={'ghost'}
          grey
          size="l"
          type="button"
        >
          {t('common::form_buttons.cancel')}
        </Button>
        <Button
          disabled={!anyTouched || submitting || lockForm}
          size="l"
          variant="primary"
          type="submit"
          bgColor={'Label-GU-Primary'}
        >
          {t(`common::form_buttons.${profileId ? 'save' : 'create'}`)}
        </Button>
      </div>
    </form>
  )
}

PaymentProfileCreate = reduxForm({
  form: 'PaymentProfileCreate',
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  forceUnregisterOnUnmount: true,
  shouldAsyncValidate: shouldAsyncValidate,
  initialValues: {
    method: null,
    details: {
      country_code: null,
      is_same_address: false,
    },
    apply_agreement: false,
  },
})(PaymentProfileCreate)

PaymentProfileCreate.propTypes = {
  initialValues: PropTypes.object,
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  form: PropTypes.string,
  anyTouched: PropTypes.bool,
  loadingSettings: PropTypes.bool,
  change: PropTypes.func,
  autofill: PropTypes.func,
  untouch: PropTypes.func,
  profileId: PropTypes.string,
  legalTypes: PropTypes.array,
  legalTypesCountryCodes: PropTypes.object,
}

export default PaymentProfileCreate
